import { jsx as _jsx } from "react/jsx-runtime";
import createCache from "@emotion/cache";
import weakMemoize from "@emotion/weak-memoize";
import { CacheProvider } from "@emotion/react";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment/index.js";
import { createTheme, ThemeProvider as MuiThemeProvider, } from "@mui/material/styles";
export { CssBaseline } from "@mui/material";
// source: https://codesandbox.io/s/p98y9o7jz0?file=/src/frame-provider.js
let memoizedCreateCacheWithContainer = weakMemoize((container) => {
    let newCache = createCache({
        key: "mui",
        prepend: true,
        // Setting to `true` solves issue with slow performance on Quote Calculation page -
        // it prevents emotion from creating thousands of style tags.
        // By default - `true` for prod, `false` for dev environment
        speedy: true,
        container,
    });
    return newCache;
});
export const ThemeProvider = ({ theme: _theme = theme, locale, container, children, }) => {
    return (
    // We use CacheProvider instead of StyledEngineProvider as it described here: https://github.com/garronej/tss-react#mui-integration
    // <StyledEngineProvider injectFirst>
    _jsx(CacheProvider, { value: memoizedCreateCacheWithContainer(container ?? document.head), children: _jsx(MuiThemeProvider, { theme: _theme, children: _jsx(LocalizationProvider, { dateAdapter: AdapterMoment, adapterLocale: locale, children: children }) }) })
    // </StyledEngineProvider>
    );
};
var Color;
(function (Color) {
    Color["white"] = "#ffffff";
    Color["black"] = "#000000";
    Color["default"] = "#377dc3";
    Color["background"] = "#fcfdff";
    Color["primary"] = "#001e64";
    // secondary = "#5087c3",
    Color["grey"] = "rgba(0, 0, 0, 0.38)";
    // greyDark = "rgba(0, 0, 0, 0.58)",
    Color["blue0"] = "#001e64";
    // blue1 = "#033099",
    // blue2 = "#285fa5",
    // blue3 = "#5087c3",
    Color["blue4"] = "#7dafd7";
    // blue5 = "#afcde6",
    // blue6 = "#c8dceb",
    // blue7 = "#d7e6f0",
    Color["blue8"] = "#e8f0f5";
    Color["blue9"] = "#f2f6fa";
    Color["blue10"] = "#f8f9fc";
    // danger = "#ff735a",
    // dangerLight = "#ff8e7a",
    // dangerDark = "#ff593b",
    // green = "#4caf50",
    // greenAlpha = "rgba(76,175,80, 0.2)",
    // greenLight = "#e6fffa",
    // greenDark = "#37c8aa",
    // red = "#ff6e78",
    // redAlpha = "rgba(255,110,120, 0.2)",
    // redLighter = "#fbedeb",
    // redLight = "#ffe1e6",
    // redDark = "#fa4664",
    // orange = "#ff9800",
    // orangeLight = "#ffaa2c",
    // orangeDark = "#da8200",
    // yellow = "#ffe178",
    // yellowLight = "#fffabe",
    // yellowDark = "#fad750",
    // turquoise = "#18A999",
    // lightBlue = "#1EA7FD",
    // purple = "#cb6bb2",
    Color["msOrange"] = "#ff735a";
    Color["msOrangeLight"] = "#ff8e7a";
    Color["msOrangeDark"] = "#ff593b";
})(Color || (Color = {}));
const baseThemeOptions = {
    components: {
        MuiFormControl: {
            defaultProps: {
                fullWidth: true,
                size: "small",
                variant: "filled",
            },
            styleOverrides: {
                root: ({ ownerState }) => ({
                    margin: "unset",
                    ...(ownerState.size === "extra-small"
                        ? {
                            "& .MuiFormHelperText-root": {
                                marginLeft: "8px",
                                marginRight: "8px",
                            },
                        }
                        : undefined),
                }),
            },
        },
        MuiTextField: {
            defaultProps: {
                fullWidth: true,
                size: "small",
                variant: "filled",
            },
            styleOverrides: {
                root: ({ ownerState }) => {
                    return {
                        ...(ownerState.size === "extra-small"
                            ? {
                                "& label": {
                                    display: "none",
                                },
                                "& input": {
                                    paddingTop: "4px !important",
                                    paddingBottom: "3px !important",
                                    paddingLeft: "4px !important",
                                    paddingRight: "4px",
                                },
                            }
                            : undefined),
                    };
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: ({ ownerState }) => ({
                    border: "none",
                    // adds safe paddings for mobile devices on a full screen
                    ...(ownerState.fullScreen && {
                        paddingTop: "env(safe-area-inset-top)",
                        paddingBottom: "env(safe-area-inset-bottom)",
                    }),
                }),
            },
            defaultProps: {
                // removes border around paper component
                PaperProps: {
                    variant: "elevation",
                    elevation: 0,
                },
            },
        },
        MuiDialogContentText: {
            defaultProps: {
                gutterBottom: true,
            },
            styleOverrides: {
                root: {
                    marginBottom: "12px",
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down("md")]: {
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingBottom: "8px",
                    },
                    [theme.breakpoints.up("md")]: {
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        paddingBottom: "12px",
                    },
                }),
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down("md")]: {
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                    },
                    [theme.breakpoints.up("md")]: {
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                    },
                }),
            },
        },
        MuiDrawer: {
            defaultProps: {
                // removes border around paper component
                PaperProps: {
                    variant: "elevation",
                    elevation: 0,
                },
            },
        },
        MuiModal: {
            defaultProps: {
                // https://help.usersnap.com/docs/track-css-styles#known-issue-with-frontend-framework-material-ui
                disableEnforceFocus: true,
            },
        },
        MuiPaper: {
            defaultProps: {
                variant: "outlined",
            },
        },
        MuiFab: {
            styleOverrides: {
                root: { boxShadow: "none !important" },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: Color.blue8,
                    "&:hover:not(.Mui-disabled):not(.Mui-focused)": {
                        backgroundColor: Color.blue9,
                    },
                    "&.Mui-focused": {
                        backgroundColor: Color.blue8,
                    },
                },
                underline: {
                    "&:before": {
                        visibility: "hidden",
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                filled: {
                    color: Color.blue4,
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: "hover",
            },
        },
        MuiChip: {
            defaultProps: {
                variant: "outlined",
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(ownerState.size === "extra-small" && {
                        padding: "0 8px",
                    }),
                    ...(ownerState.size === "extra-large" && {
                        padding: "10px 12px",
                        fontSize: "1rem",
                    }),
                }),
                startIcon: ({ ownerState }) => ({
                    ...(ownerState.size === "small" && {
                        marginRight: "4px",
                    }),
                    ...(ownerState.size === "extra-small" && {
                        marginRight: "4px",
                        [".MuiSvgIcon-root"]: { fontSize: "1.15rem" },
                    }),
                }),
                endIcon: ({ ownerState }) => ({
                    ...(ownerState.size === "small" && {
                        marginLeft: "4px",
                    }),
                    ...(ownerState.size === "extra-small" && {
                        marginLeft: "4px",
                        [".MuiSvgIcon-root"]: { fontSize: "1.15rem" },
                    }),
                }),
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(ownerState.size === "extra-small" && {
                        ".MuiToggleButton": {
                            padding: "3px 8px",
                        },
                    }),
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(ownerState.size === "extra-small" && {
                        padding: "3px 8px",
                    }),
                }),
            },
        },
        MuiIcon: {
            styleOverrides: {
                root: {
                    display: "inline-flex",
                    flexShrink: 0,
                    flexGrow: 0,
                    justifyContent: "center",
                    alignItems: "center",
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    display: "inline-flex",
                    flexShrink: 0,
                    flexGrow: 0,
                    justifyContent: "center",
                    alignItems: "center",
                    ...(ownerState.size === "extra-small" && {
                        padding: "4px",
                    }),
                }),
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    margin: "unset",
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                // margin: "unset", // FIXME: this is not working well with `gutterBottom` prop
                // whiteSpace: "pre-wrap", // FIXME: why did we have this?
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: "inherit",
                    minWidth: 48,
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(ownerState.size === "extra-small"
                        ? {
                            [".MuiTableCell-root"]: {
                                padding: "8px",
                                fontSize: "0.875rem",
                            },
                        }
                        : undefined),
                }),
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    color: Color.blue0,
                    "&:not(:last-child)": {
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&:last-child td": {
                        borderBottom: 0,
                    },
                    "&.Mui-selected": {
                        backgroundColor: "unset",
                        border: `2px solid ${Color.default}`,
                        cursor: "default",
                    },
                    "&.Mui-selected:hover": {
                        backgroundColor: "unset",
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: "1rem",
                    lineHeight: 1.3333,
                    color: "inherit",
                },
                body: {
                    fontWeight: 400,
                },
                sizeSmall: {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    fontSize: "0.875rem",
                },
            },
        },
        MuiTablePagination: {
            defaultProps: {
                SelectProps: { fullWidth: false },
            },
            styleOverrides: {
                root: {
                    flexGrow: 0,
                    flexShrink: 0,
                },
                toolbar: { paddingLeft: 0, paddingRight: 0, minHeight: 40 },
                actions: ({ theme }) => ({
                    "& > button": { padding: 8 },
                    [theme.breakpoints.down("md")]: {
                        marginLeft: "12px",
                    },
                }),
                select: ({ theme }) => ({
                    padding: "3px 0 3px 5px",
                    ["&, &:hover, &:active, &:focus"]: {
                        borderRadius: `${theme.shape.borderRadius}px`,
                    },
                    [theme.breakpoints.down("md")]: {
                        marginRight: "8px",
                    },
                    transition: "background-color 0.1s ease-out",
                    ["&:hover"]: {
                        backgroundColor: theme.palette.action.hover,
                    },
                    ["&:active"]: {
                        backgroundColor: theme.palette.action.selected,
                    },
                }),
            },
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    paddingLeft: "6px",
                    paddingRight: "6px",
                    minWidth: "62px",
                },
            },
        },
        MuiImageList: {
            styleOverrides: {
                root: {
                    padding: 0,
                    margin: 0,
                },
            },
        },
        // @ts-ignore set default white background; add `cursor: pointer` to rows; remove focus outline from cells
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    backgroundColor: Color.white,
                },
                row: {
                    '&:not([data-id*="auto-generated-row"])': {
                        cursor: "pointer",
                    },
                },
                cell: {
                    // to imitate behaviour v6
                    display: "flex",
                    alignItems: "center",
                    "&:focus, &:focus-within": {
                        outline: "none",
                    },
                },
                columnHeader: {
                    "&:focus, &:focus-within": {
                        outline: "none",
                    },
                },
                treeDataGroupingCell: {
                    fontWeight: 500,
                },
                // @ts-ignore
                virtualScroller: (props) => {
                    if (props?.className === "MuiDataGrid-virtualScroller" &&
                        (props?.ownerState?.rowCount === 0 ||
                            props?.ownerState?.rows?.length === 0))
                        return {
                            minHeight: props?.ownerState?.autoHeight ? "72px" : "132px",
                        };
                },
            },
        },
        // @ts-ignore set default loading position
        MuiLoadingButton: {
            defaultProps: {
                loadingPosition: "end",
            },
            styleOverrides: {
                // @ts-ignore adding padding for loaders
                root: ({ ownerState }) => ({
                    ...(ownerState.loading && ownerState.loadingPosition === "end"
                        ? { paddingRight: "40px" }
                        : undefined),
                    ...(ownerState.loading && ownerState.loadingPosition === "start"
                        ? { paddingLeft: "40px" }
                        : undefined),
                }),
            },
        },
        MuiContainer: {
            defaultProps: {
                maxWidth: false,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    // margin: 0, // we'll set margin to 0 inside a specific places
                    padding: 0, // padding should come only from PageContainer
                    paddingLeft: 0,
                    paddingRight: 0,
                    [theme.breakpoints.up("sm")]: {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                }),
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    justifyContent: "space-between",
                    alignItems: "center",
                },
                regular: ({ theme }) => ({
                    [theme.breakpoints.up("sm")]: {
                        minHeight: 60,
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                    },
                }),
                dense: ({ theme }) => ({
                    minHeight: 36,
                    [theme.breakpoints.up("sm")]: {
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                    },
                }),
            },
        },
        MuiAppBar: {
            defaultProps: {
                variant: "outlined",
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    return {
                        ...(ownerState.size === "extra-small" && ownerState.startAdornment
                            ? {
                                paddingLeft: "8px !important",
                            }
                            : undefined),
                        ...(ownerState.size === "extra-small" && ownerState.endAdornment
                            ? {
                                paddingRight: "8px !important",
                            }
                            : undefined),
                    };
                },
                input: ({ ownerState }) => {
                    return {
                        // fix for datepicker in Safari to show it in grey when no date selected
                        ...(ownerState.type === "date" && ownerState.value === ""
                            ? { color: Color.grey }
                            : undefined),
                        ...(ownerState.size === "extra-small"
                            ? {
                                paddingTop: "4px !important",
                                paddingBottom: "3px !important",
                                paddingLeft: "4px !important",
                                paddingRight: "4px",
                            }
                            : undefined),
                    };
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                fullWidth: true,
                size: "small",
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                fullWidth: true,
                openOnFocus: true,
            },
            styleOverrides: {
                inputRoot: ({ ownerState }) => {
                    return {
                        ...(ownerState.size === "extra-small"
                            ? {
                                paddingTop: "0px !important",
                                paddingBottom: "0px !important",
                                paddingLeft: "0px !important",
                            }
                            : undefined),
                    };
                },
                listbox: {
                    '& .MuiAutocomplete-option.Mui-focused[aria-selected="true"]': {
                        background: "rgba(0, 30, 100, 0.14) !important",
                    },
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    return {
                        ...(ownerState.fontSize === "extra-small"
                            ? {
                                fontSize: "0.95rem",
                            }
                            : undefined),
                    };
                },
            },
        },
    },
    typography: {
        h1: {
            fontSize: "1.5rem",
            fontWeight: 400,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: "1.375rem",
            fontWeight: 500,
            lineHeight: 1.2,
        },
        h3: {
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.3333,
        },
        h4: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.3333,
        },
        h5: {
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: 1.3333,
        },
        h6: {
            fontWeight: 500,
            lineHeight: 1.3333,
        },
        caption: {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 1.3333,
        },
    },
    shape: {
        borderRadius: 4,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 984,
            tablet: 984,
            lg: 1200,
            desktop: 1200,
            xl: 1536,
            xxl: 1920,
        },
    },
    navBar: {
        expandedWidth: 310,
        collapsedWidth: 64,
        itemHeight: 52,
    },
    layout: {
        galleryHeight: {
            xs: 200,
            sm: 248,
            md: 400,
            lg: 600,
        },
        imageSize: {
            xs: 48,
            sm: 64,
            md: 84,
            lg: 92,
            xl: 108,
        },
        columnWidth: {
            lg: 456,
            md: 372,
            sm: 340,
            xs: 296,
            xxs: 272,
        },
        pageWidth: {
            oneColumnWithPreview: 1300, // 792 (preview width) + 48 (two horizontal paddings) + 456 (one column) + 64 (left menu)
            twoColumnsWithPreview: 1756, // 792 (preview width) + 48 (two horizontal paddings) + 2 * 456 (two columns) + 64 (left menu)
        },
        tableWidth: {
            preview: 732,
        },
        tableCellWidth: {
            xs: 70,
            sm: 85,
            md: 110,
            lg: 125,
            xl: 140,
        },
        listItemMinWidth: {
            xs: 28,
            sm: 40,
            md: 48,
            lg: 62,
        },
        filters: {
            popoverWidth: {
                md: 220,
                lg: 280,
                xl: 340,
                xxl: 400,
            },
        },
        searchFieldWidth: {
            xs: 240,
            sm: 320,
            md: 420,
            lg: 600,
        },
        formSubmitWidth: {
            md: 340,
        },
    },
    custom: {
        colors: {
            selectedOverlayBackground: "rgba(80, 135, 195, 0.2)",
            loadingOverlayBackground: "rgba(255, 255, 255, 0.5)",
            templateHeaderBackground: "#F0FAFF",
        },
    },
};
export const theme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: "light",
        common: { white: Color.white, black: Color.black },
        primary: { main: Color.blue0, contrastText: Color.white },
        secondary: { main: Color.default, contrastText: Color.white },
        background: { default: Color.background },
        text: {
            primary: Color.blue0,
            secondary: Color.default,
            disabled: Color.grey,
        },
        divider: "rgba(0, 0, 0, 0.12)",
        orange: {
            main: Color.msOrange,
            light: Color.msOrangeLight,
            dark: Color.msOrangeDark,
            contrastText: Color.white,
        },
        blue: {
            default: Color.blue8,
            paper: Color.blue8,
        },
        black: {
            main: Color.black,
            light: Color.black,
            dark: Color.black,
            contrastText: Color.white,
        },
    },
});
export const inverseTheme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: "dark",
        common: { white: Color.white, black: Color.black },
        primary: {
            main: Color.white,
            light: "rgba(255, 255, 255, 0.9)",
            dark: "rgba(255, 255, 255, 0.8)",
            contrastText: Color.primary,
        },
        secondary: {
            main: Color.white,
            light: "rgba(255, 255, 255, 0.9)",
            dark: "rgba(255, 255, 255, 0.8)",
            contrastText: Color.default,
        },
        background: { default: Color.background },
        text: {
            primary: Color.white,
            secondary: "rgba(255, 255, 255, 0.7)",
            disabled: "rgba(255, 255, 255, 0.5)",
        },
        divider: "rgba(0, 0, 0, 0.12)",
        orange: {
            main: Color.msOrange,
            light: Color.msOrangeLight,
            dark: Color.msOrangeDark,
            contrastText: Color.white,
        },
        blue: {
            default: Color.blue8,
            paper: Color.blue8,
        },
        black: {
            main: Color.white,
            light: Color.white,
            dark: Color.white,
            contrastText: Color.black,
        },
    },
});
//# sourceMappingURL=Theme.js.map